import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import type { DataTableHeader } from 'vuetify';
import { Currency, TradingType } from '@/constants';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { TurnoverEventItem } from '@/models/turnoverEvent';
import { TurnoverEventStats } from '@/models/turnoverEventStats';
import { createTurnoverGamePageHeaders } from '@/pages/stats/turnover/game/TurnoverGamePageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import ErrorService from '@/services/errorService';
import { turnoverService } from '@/services/turnoverService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { currencyDictionary } from '@/stores/simpleDictionary/currency';
import { IPageOptions, ITurnoverDetailState, ITurnoverEventFilters, ITurnoverGameDetailStats } from '@/types';
import { ITurnoverEventStatsApi } from '@/types/turnoverEventStats';

const defaultSortBy = [{ key: 'date', order: 'asc' as const }];

function createTurnoverEventStoreSetup(storeId: string) {
    // --- state ---
    const defaultStats: ITurnoverEventStatsApi = {
        user_count: 0,
        settled_bet_count: 0,
        unsettled_bet_count: 0,
        bet_count: 0,
        stake_sum: 0,
        balance: 0,
    };
    const items = shallowRef<TurnoverEventItem[]>([]);
    const stats = shallowRef<TurnoverEventStats>(new TurnoverEventStats(defaultStats));
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const detailState = ref<Record<string, ITurnoverDetailState<ITurnoverGameDetailStats>>>({});

    // --- filters ---
    const filters = reactive<ITurnoverEventFilters>({
        sportId: createRequiredSportFilter(),
        eventDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'turnoverEvent.gameDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        eventSearch: {
            value: null,
            title: 'common.search',
            allowSaveState: false,
        },
        betTradingTypeId: {
            value: TradingType.BetOrder,
            title: 'common.tradingType',
            allowSaveState: true,
        },
        userCurrencyId: {
            value: null,
            title: 'common.playerCurrency',
            allowSaveState: true,
        },
        displayedCurrencyId: {
            value: Currency.Brl,
            title: 'common.displayedCurrency',
            allowSaveState: true,
            disableClear: true,
        },
        betOpenStatus: {
            value: null,
            title: 'common.betStatus',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.eventDate.value = todayDateRangeInUserTimeZone();
        filters.eventSearch.value = null;
        filters.betTradingTypeId.value = TradingType.BetOrder;
        filters.userCurrencyId.value = null;
        filters.displayedCurrencyId.value = Currency.Brl;
        filters.betOpenStatus.value = null;
        filters.betEventPeriodId.value = null;
        filters.betPlatformId.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createTurnoverGamePageHeaders(currencyDictionary, filters.displayedCurrencyId.value));

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'turnoverEvent',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.eventSearch.value) {
                // filtersToSend.eventDate = { ...filtersToSend.eventDate, value: null };
            }

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country'],
            };

            const response = await turnoverService.event(params);
            items.value = response.data.rows.map((item) => new TurnoverEventItem(item));
            stats.value = new TurnoverEventStats(response.data.stats);
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function getDetailState(item: TurnoverEventItem) {
        const key = item.customEventId;
        detailState.value[key] ??= { loading: false, loaded: false, error: false, rows: [] };
        return detailState.value[key];
    }

    function getGameDetailFilters(item: TurnoverEventItem) {
        return {
            ...filters,
            eventCustomEventId: { value: item.customEventId },
        };
    }

    async function loadGameDetail(item: TurnoverEventItem) {
        const state = getDetailState(item);
        if (state.loading) return;

        state.error = false;
        state.loaded = false;
        state.rows = [];
        state.loading = true;

        try {
            const response = await turnoverService.gameDetail({
                filters: getGameDetailFilters(item) as any,
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
                requestKey: `turnover-game-detail-${item.customEventId}`,
            });
            state.rows = Array.isArray((response.data as any).rows) ? ((response.data as any).rows as ITurnoverGameDetailStats[]) : [];
            state.loaded = true;
        } catch (error) {
            state.error = true;
            ErrorService.log(error);
        } finally {
            state.loading = false;
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        detailState.value = {};
        stats.value = new TurnoverEventStats(defaultStats);
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        detailState,
        stats,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        getDetailState,
        loadGameDetail,
        updateOptions,
        clearData,
    };
}

export function useTurnoverEventStore(storeId: string) {
    const useStore = defineStore(storeId, () => createTurnoverEventStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'turnoverEvent'),
    });
    return useStore();
}

export type TurnoverEventStoreSetup = ReturnType<typeof useTurnoverEventStore>;
